import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useCreatePensionContract } from "../hooks/pension";
import type { CreatePensionContractInput, PensionContractKind } from "../lib/pension";

const KINDS: PensionContractKind[] = ["GESETZLICH", "BAV", "RIESTER", "RUERUP", "PRIVAT"];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function PensionNew() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const create = useCreatePensionContract();
  const [error, setError] = useState<string | undefined>();

  const [name, setName] = useState("");
  const [kind, setKind] = useState<PensionContractKind>("PRIVAT");
  const [provider, setProvider] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [monthlyContributionEur, setMonthlyContributionEur] = useState("");
  const [expectedRetirementAge, setExpectedRetirementAge] = useState("67");
  const [projectedMonthlyPensionEur, setProjectedMonthlyPensionEur] = useState("");
  const [notes, setNotes] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    const input: CreatePensionContractInput = {
      name: name.trim(),
      kind,
      provider: provider.trim() || undefined,
      contractNumber: contractNumber.trim() || undefined,
      startDate,
      monthlyContributionCents: monthlyContributionEur
        ? Math.round(Number(monthlyContributionEur) * 100)
        : undefined,
      expectedRetirementAge: expectedRetirementAge ? Number(expectedRetirementAge) : undefined,
      projectedMonthlyPensionCents: projectedMonthlyPensionEur
        ? Math.round(Number(projectedMonthlyPensionEur) * 100)
        : undefined,
      notes: notes.trim() || undefined,
    };
    try {
      const created = await create.mutateAsync(input);
      nav(`/pension/${created.id}`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="space-y-4 max-w-2xl">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("pension.new.title")}</h1>
        <Link to="/pension" className="text-sm text-blue-600 underline">
          {t("pension.new.back")}
        </Link>
      </header>

      <form onSubmit={handleSubmit} className="bg-white border rounded p-4 space-y-3">
        <Field label={t("pension.form.name")}>
          <input
            type="text"
            required
            className="border rounded px-2 py-1 w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("pension.form.kind")}>
            <select
              className="border rounded px-2 py-1 w-full"
              value={kind}
              onChange={(e) => setKind(e.target.value as PensionContractKind)}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`pension.kind.${k}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("pension.form.provider")}>
            <input
              type="text"
              className="border rounded px-2 py-1 w-full"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            />
          </Field>
        </div>
        <Field label={t("pension.form.contract_number")}>
          <input
            type="text"
            className="border rounded px-2 py-1 w-full"
            value={contractNumber}
            onChange={(e) => setContractNumber(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("pension.form.start_date")}>
            <input
              type="date"
              required
              className="border rounded px-2 py-1 w-full"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Field>
          <Field label={t("pension.form.expected_retirement_age")}>
            <input
              type="number"
              min={50}
              max={80}
              className="border rounded px-2 py-1 w-full"
              value={expectedRetirementAge}
              onChange={(e) => setExpectedRetirementAge(e.target.value)}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("pension.form.monthly_contribution")}>
            <input
              type="number"
              step="0.01"
              min={0}
              className="border rounded px-2 py-1 w-full"
              value={monthlyContributionEur}
              onChange={(e) => setMonthlyContributionEur(e.target.value)}
            />
          </Field>
          <Field label={t("pension.form.projected_pension")}>
            <input
              type="number"
              step="0.01"
              min={0}
              className="border rounded px-2 py-1 w-full"
              value={projectedMonthlyPensionEur}
              onChange={(e) => setProjectedMonthlyPensionEur(e.target.value)}
            />
          </Field>
        </div>
        <Field label={t("pension.form.notes")}>
          <textarea
            className="border rounded px-2 py-1 w-full"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        {error && <p className="text-red-700 text-sm">{error}</p>}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={create.isPending}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {t("pension.form.submit")}
          </button>
          <Link to="/pension" className="px-3 py-1 border rounded">
            {t("pension.form.cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="block text-sm">
      <span className="text-slate-600">{label}</span>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
